import type { AccountInfo } from '@azure/msal-browser';
import { isUserRole, type User, type UserRole } from '@domain/models/user.interface';
import type { ThemeName } from '@domain/models/theme.enum';

/**
 * Shape of the claim subset we read off an ID token. All fields
 * are optional — Entra tenants may or may not emit them depending
 * on app registration and consent.
 */
interface IdTokenClaims {
  readonly oid?: string;
  readonly sub?: string;
  readonly tid?: string;
  readonly name?: string;
  readonly preferred_username?: string;
  readonly upn?: string;
  readonly email?: string;
  readonly roles?: readonly string[];
  readonly extension_Theme?: string;
  readonly extension_Language?: string;
}

/**
 * Maps an MSAL `AccountInfo` (+ its `idTokenClaims`) into the UI
 * `User` domain model. Invalid / unknown role strings are filtered
 * out rather than throwing — unknown roles are treated as "no
 * privilege".
 */
export function userFromAccount(account: AccountInfo): User {
  const claims = (account.idTokenClaims ?? {}) as IdTokenClaims;

  const rawRoles = claims.roles ?? [];
  const roles: UserRole[] = rawRoles.filter(isUserRole);

  const username =
    claims.preferred_username ??
    claims.upn ??
    claims.email ??
    account.username ??
    '';

  const displayName = claims.name ?? account.name ?? username;

  const language = parseLanguage(claims.extension_Language);
  const theme = parseTheme(claims.extension_Theme);

  return {
    id: account.homeAccountId,
    username,
    displayName,
    roles,
    tenantId: account.tenantId,
    ...(language ? { language } : {}),
    ...(theme ? { theme } : {}),
  };
}

function parseLanguage(value: string | undefined): User['language'] | undefined {
  if (value === 'en-GB' || value === 'nl-NL') {
    return value;
  }
  return undefined;
}

function parseTheme(value: string | undefined): ThemeName | undefined {
  if (value === 'clinical' || value === 'aurora') {
    return value;
  }
  return undefined;
}
